import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DropdownModule, DropdownTriggerForDirective, IconModule, ToastModule, ToastType } from '@hglobal/common';
import { TranslateModule } from '@ngx-translate/core';
import { interval } from 'rxjs';

import { SpinnerComponent } from '@pp/common';
import { FaviconService, GlobalToastService } from '@pp/core/services';

import { NotificationsHistoryComponent } from './notifications-history/notifications-history.component';
import { NotificationsApi } from './notifications.api';
import { NOTIFICATIONS_API } from './notifications.api.interface';
import { Notification } from './notifications.entities';
import { NotificationsStore } from './notifications.store';
import { ThreeDsToastComponent } from './three-ds-toast/three-ds-toast.component';

const REFRESH_TIME = 10 * 1000;

@Component({
  selector: 'pp-notifications',
  imports: [
    NotificationsHistoryComponent,
    IconModule,
    DropdownModule,
    TranslateModule,
    ToastModule,
    SpinnerComponent,
    ThreeDsToastComponent,
  ],
  providers: [
    {
      provide: NOTIFICATIONS_API,
      useClass: NotificationsApi,
    },
    NotificationsStore,
  ],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent implements OnInit, AfterViewInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(NotificationsStore);
  readonly #globalToastService = inject(GlobalToastService);
  readonly #faviconService = inject(FaviconService);
  readonly #offsets = {
    yTopOfScreen: -48,
    yUnderTrigger: 16,
    xEndOfTrigger: 0,
    xEndOfScreenForMob: 8,
    xEndOfScreenForTablet: 48,
  };

  readonly breakpoints = input<{
    isDesktop: boolean;
    isTablet: boolean;
  }>();
  readonly dropdown = viewChild(DropdownTriggerForDirective);
  readonly messageTpl = viewChild<TemplateRef<unknown>>('messageToastTpl');
  readonly threeDsTpl = viewChild<TemplateRef<unknown>>('threeDsToastTpl');

  readonly totalUnreadCount = this.#store.totalUnreadCount;
  readonly markAsReadLoading = this.#store.markAsReadLoading;
  readonly openedSubject = signal(false);

  readonly offsetY = computed(() => (this.breakpoints()?.isTablet ? this.#offsets.yUnderTrigger : this.#offsets.yTopOfScreen));
  readonly offsetX = computed(() => {
    const breakpoints = this.breakpoints();

    if (breakpoints?.isDesktop) {
      return this.#offsets.xEndOfTrigger;
    }
    if (breakpoints?.isTablet) {
      return this.#offsets.xEndOfScreenForTablet;
    }
    return this.#offsets.xEndOfScreenForMob;
  });

  newMessagesEffect = effect(() => {
    const newMessages = this.#store.newMessages();

    untracked(() => this.#onNewMessagesReceived(newMessages));
  });

  newThreeDsEffect = effect(() => {
    const newThreeDs = this.#store.newThreeDs();

    untracked(() => this.#onNewThreeDsReceived(newThreeDs));
  });

  ngOnInit() {
    this.#subscribeToRefreshUnreadCount();
  }

  ngAfterViewInit(): void {
    this.dropdown().closed$.subscribe(() => {
      this.openedSubject.set(false);
      this.#store.clearDataOnDialogClose();
    });
  }

  showOverlay(): void {
    this.openedSubject.set(true);
  }

  closeDropdown(): void {
    this.dropdown().closeDropdown();
  }

  markAsRead(message: Notification): void {
    if (message.read) {
      return;
    }
    message.read = true;
    this.#store.readMessage(message);
  }

  closeToast(event: { isManual: boolean }, message: Notification): void {
    if (!event.isManual) {
      return;
    }

    this.markAsRead(message);
  }

  #subscribeToRefreshUnreadCount(): void {
    interval(REFRESH_TIME)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#store.refreshUnreadCount());
  }

  #onNewMessagesReceived(messages: Notification[]): void {
    if (!messages || this.#store.isNotificationsDisabled()) {
      return;
    }

    this.#showNewNotificationsToasts(messages, this.messageTpl());
    this.#store.clearNewMessages();
  }

  #onNewThreeDsReceived(threeDs: Notification[]): void {
    if (!threeDs) {
      return;
    }

    this.#showNewNotificationsToasts(threeDs, this.threeDsTpl());
    this.#faviconService.threeDsNotificationsReceived();
    this.#store.clearNewThreeDs();
  }

  #showNewNotificationsToasts(notifications: Notification[], tpl: TemplateRef<unknown>): void {
    for (const notification of notifications) {
      this.#showNotificationToast(notification, tpl);
    }
  }

  #showNotificationToast(notification: Notification, tpl: TemplateRef<unknown>): void {
    this.#globalToastService.showCustom({
      tpl,
      type: ToastType.Info,
      title: notification.notificationType,
      message: notification.message,
      entity: { ...notification },
    });
  }
}
